import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, navigate } from '@reach/router';
import dayjs from 'dayjs';

import { Alert, AlertTitle } from '@material-ui/lab';
import { LinearProgress, Grid, TextField } from '@material-ui/core';

import { FirebaseContext } from '../../../services/firebase/FirebaseContext';
import FireDataContext from '../../../services/firebase/FireDataContext';
import InvoicePreview from './InvoicePreview';
import MemberMiniMenu from '../menus/MemberMiniMenu';
import XeroInvoiceApi from '../../admin/xero/XeroInvoiceApi';
import Button from '@material-ui/core/Button'
import AuthContext from '../../../services/firebase/AuthContext';
import { getSalesInvoiceRecipientInfo } from './InvoiceData';
import InvoiceEditor from './InvoiceEditor';

const InvoicePage = ({ path }) => {
    const location = useLocation();
    const firebase = useContext(FirebaseContext);
    const db = useContext(FireDataContext);
    const { currentUser } = useContext(AuthContext);
    const api = new XeroInvoiceApi({ firebase });

    const isAdmin = currentUser?.roles?.includes('admin');

    const [invoice, setInvoice] = useState();
    const [member, setMember] = useState();
    const [message, setMessage] = useState();
    const [busy, setBusy] = useState(false);

    const invoiceRef = useRef();

    const builder = {
        sales: api.buildSalesInvoice,
        purchase: api.buildMemberInvoice,
    }

    const { pathname } = location;
    const relatedInvoiceUrl = /sales_invoices/i.test(pathname) ? pathname.replace('sales_invoice', 'purchase_invoice') : pathname.replace('purchase_invoice', 'sales_invoice');

    useEffect(() => {
        (async () => {
            try {
                const inv = await db.get({ path });
                const org = await db.get({ path: `organizations/${inv.referral.organizationId}` });

                // WTAF !!! we don't actually have the intermediary id on the invoice !!!
                const user = await db.get({ path: `users/${inv.created.user.id}` });

                setMember(user);
                setInvoice({ ...inv, referrer: inv.referral.referrer, organization: org, member: user });
            }
            catch (error) {
                setMessage({
                    title: 'System Error',
                    severity: 'error',
                    text: error.message
                });
            }

        })();

    }, [db, path]);

    const handleXeroSubmit = () => {

        var xeroContactId = undefined;
        var url = undefined;
        var accountCode = undefined;

        if (invoice.invoiceType === 'sales') {
            xeroContactId = (invoice.timesheet.invoicee || getSalesInvoiceRecipientInfo({
                referrer: invoice.referrer,
                organization: invoice.organization,
                tariff: invoice.timesheet.tariff,
                items: invoice.timesheet.items
            }))?.xeroContactId;
            url = `https://ti-coop.web.app/organizations/WHuoyDs7zcB8wUU7euiC/sales_invoices/${invoice.timesheet.id}`;
            accountCode = '200';
        }
        if (invoice.invoiceType === 'purchase') {
            xeroContactId = member?.xeroContactId;
            url = `https://ti-coop.web.app/organizations/WHuoyDs7zcB8wUU7euiC/purchase_invoices/${invoice.timesheet.id}`;
            accountCode = '310';
        }

        const date = dayjs(invoice.created.ts);

        const inv = builder[invoice.invoiceType]({
            vatNumber: invoice.intermediary?.profile?.vatNumber,
            xeroContactId,
            date,
            dueDate: date.add(14, 'days'),
            url,
            bookingReference: invoice.referral.bookingReference,
            status: 'SUBMITTED',
            items: invoice.timesheet.items,
            tariff: invoice.timesheet.tariff,
            accountCode
        })

        setBusy(true);

        api.createInvoice(inv)
            .then(result => {

                console.log('result', result, invoice._docPath);

                db.update({
                    path: invoice._docPath,
                    payload: {
                        xeroInvoiceId: result.xeroInvoiceId,
                        reference: result.reference ?? invoice.reference ?? invoice.referral.bookingReference,
                        ts: result.ts
                    }
                })
                    .then(() => {
                        setMessage({
                            title: 'Success!',
                            severity: 'success',
                            text: JSON.stringify(result)
                        });

                        setBusy(false);
                    })
                    .catch(error => {
                        setMessage({
                            title: 'Error updating database',
                            severity: 'error',
                            text: JSON.stringify(error)
                        });
                    })
            })
            .catch(error => {
                setMessage({
                    title: 'System Error',
                    severity: 'error',
                    text: JSON.stringify(error)
                });
            })
    }

    const handleXeroInvoiceChange = () => {
        db.update({
            path: invoice._docPath,
            payload: {
                xeroInvoiceId: invoiceRef.current.value || '',
                ts: dayjs().valueOf(),
                uid: currentUser.uid
            }
        })
            .then(() => {
                setMessage({
                    title: 'Success',
                    severity: 'success',
                    text: 'Yay!'
                });
            })
            .catch(error => {
                setMessage({
                    title: 'Update Error',
                    severity: 'error',
                    text: JSON.stringify(error)
                });
            })
    }

    if (message) return (
        <MemberMiniMenu>
            <Alert severity={message.severity} variant="filled"><AlertTitle>{message.title}</AlertTitle>{message.text}</Alert>
        </MemberMiniMenu>
    );

    if (!invoice) return <></>;



    return (
        <MemberMiniMenu>
            {
                message && <Alert severity={message.severity} variant="filled"><AlertTitle>{message.title}</AlertTitle>{message.text}</Alert>
            }
            {
                isAdmin && (!invoice.xeroInvoiceId || invoice.xeroInvoiceId === '!?') &&
                <div style={{ marginBottom: '1rem' }}>
                    <Button variant="contained" color="secondary" onClick={handleXeroSubmit} disabled={busy}>
                        CREATE XERO INVOICE
                    </Button>
                </div>
            }
            {
                busy && <LinearProgress style={{ marginBottom: '1rem' }} />
            }
            <div style={{ marginBottom: '1rem' }}>
                <Button variant="contained" color="primary" onClick={() => navigate(relatedInvoiceUrl)} disabled={busy}>
                    VIEW {invoice.invoiceType === 'sales' ? 'Member' : 'Coop'} INVOICE
                </Button>
            </div>
            <InvoicePreview digest={invoice} invoiceType={invoice.invoiceType} reference={invoice.reference} onSubmit={handleXeroSubmit} />
            <Grid container spacing={1} style={{ marginTop: '1rem' }}>
                <Grid item xs={4}>
                    <TextField
                        inputRef={invoiceRef}
                        defaultValue={invoice?.xeroInvoiceId ?? ''}
                        label="XERO Invoice ID"
                        onChange={handleXeroInvoiceChange}
                        fullWidth
                        variant="filled"
                    />
                </Grid>
            </Grid>
            {isAdmin && <InvoiceEditor path={path} />}
        </MemberMiniMenu>
    );
}

export default InvoicePage;